// src/components/VideoDetailed.jsx
import React, { useState, useEffect, useContext } from 'react'
import { convertFromRaw } from 'draft-js'
import { stateToHTML } from 'draft-js-export-html'
import { getAccessToken } from '../services/AuthService'
import { useParams, Link } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import axios from 'axios'
import { UserContext } from '../context'
import './VideoDetailed.css'

const VideoDetailed = () => {
  const [state] = useContext(UserContext)

  const [video, setVideo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [locked, setLocked] = useState(false)     // 401 case
  const [notFound, setNotFound] = useState(false) // 404 case

  const { video_id, video_title } = useParams()   // optional :video_title in route

  useEffect(() => {
    const getVideo = async () => {
      setLoading(true)
      setLocked(false)
      setNotFound(false)

      const apiUrl = `${process.env.REACT_APP_BASE_URL}/api/video_detail/${video_id}`
      const token = getAccessToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}

      try {
        // Don't throw on 401/404 — we want to branch on status in UI.
        const res = await axios.get(apiUrl, {
          headers,
          timeout: 5000,
          validateStatus: () => true,
        })

        if (res.status === 200) {
          setVideo(res.data || null)
        } else if (res.status === 401) {
          setLocked(true)
          setVideo(null) // backend doesn't include metadata on 401
        } else if (res.status === 404) {
          setNotFound(true)
          setVideo(null)
        } else {
          // other errors -> treat like not found (or customize)
          setNotFound(true)
          setVideo(null)
        }
      } catch (err) {
        console.error('Error obtaining video:', err)
        setNotFound(true)
      } finally {
        setLoading(false)
      }
    }

    getVideo()
  }, [video_id])

  useEffect(() => {
    // when loading becomes false, try to scroll to the player
    if (!loading) {
      const el = document.getElementById('vd-title')
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // top of the screen
        })
      }
    }
  }, [loading, video_id])

  const convertRawDataToDescription = (rawData) => {
    try {
      const parsed = JSON.parse(rawData)
      return stateToHTML(convertFromRaw(parsed))
    } catch {
      return ''
    }
  }

  const canWatch =
    !!state.user?.active || !!state.user?.is_staff || !!video?.free

  if (loading) {
    return (
      <div className="vd">
        <div className="vd__shell">
          <div className="vd__hero skeleton" />
          <div className="vd__grid">
            <div className="vd__main skeleton" />
            <aside className="vd__side skeleton" />
          </div>
        </div>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="vd">
        <div className="vd__empty">
          <h2>No encontramos este video</h2>
          <p>Revisa el enlace o vuelve al catálogo.</p>
          <Link className="vd__link" to="/videos">Ver más videos</Link>
        </div>
      </div>
    )
  }

  // We may not have video data on 401; use route title as a fallback label
  const title = video?.title || video_title || 'Contenido bloqueado'

  return (
    <div className="vd">
      <div className="vd__shell">
        {/* HERO */}
        <header className="vd__hero">
          <div className="vd__eyebrow">
            {video?.free && <span className="vd__badge">Gratis</span>}
            {video?.categories?.[0]?.title && (
              <span className="vd__chip">{video.categories[0].title}</span>
            )}
          </div>
          <h1 className="vd__title" id="vd-title" >{title}</h1>
          <div className="vd__meta">
            {video?.date_of_creation && (
              <span className="vd__meta-item">
                Publicado: {video.date_of_creation}
              </span>
            )}
          </div>
        </header>

        {/* CONTENT GRID */}
        <div className="vd__grid">
          {/* LEFT: Player + Description */}
          <main className="vd__main">
            <section className="vd__player card" id="vd-player">
              {canWatch ? (
                <div
                  className="vd__embed"
                  dangerouslySetInnerHTML={{ __html: video.url }}
                />
              ) : (
                <div className="vd__locked">
                  <div className="vd__locked-media">
                    <div className="vd__locked-blur" />
                    <div className="vd__locked-inner">
                      <div className="vd__play" aria-hidden>▶</div>
                      <h3>Suscríbete para ver este video</h3>
                      <p className="vd__locked-text">
                        Accede a todas las clases, sin límites.
                      </p>
                      <LinkContainer to="/payment-methods">
                        <Button className="vd__cta">Inscríbete</Button>
                      </LinkContainer>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {video?.description && (
              <section className="vd__desc card">
                <h2 className="vd__section-title">Descripción</h2>
                <div
                  className="vd__richtext"
                  dangerouslySetInnerHTML={{
                    __html: convertRawDataToDescription(video.description),
                  }}
                />
              </section>
            )}
          </main>

          {/* RIGHT: Sticky meta / actions */}
          <aside className="vd__side">
            <div className="vd__side-card card">
              <div className="vd__side-row">
                <span className="vd__side-label">Acceso</span>
                <span className={`vd__pill ${video?.free ? 'is-free' : 'is-premium'}`}>
                  {video?.free ? 'Gratis' : 'Premium'}
                </span>
              </div>

              {video?.categories?.length > 0 && (
                <div className="vd__side-row">
                  <span className="vd__side-label">Categoría</span>
                  <span className="vd__side-value">{video.categories[0].title}</span>
                </div>
              )}

              {state.user?.is_staff && (
                <Link
                  className="vd__side-edit"
                  to={`/video-update/${video_id}/${encodeURIComponent(title)}`}
                >
                  Editar video
                </Link>
              )}
            </div>

            {!canWatch && (
              <div className="vd__side-cta card">
                <h3>Desbloquea este contenido</h3>
                <p>Únete y disfruta de todas las clases sin restricciones.</p>
                <LinkContainer to="/payment-methods">
                  <Button className="vd__cta w-100">Suscribirme ahora</Button>
                </LinkContainer>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  )
}

export default VideoDetailed
